/**
 * KAN-735: раздел "Расходы" (только владелец): статьи за месяц (прокси, покупка
 * аккаунтов, токены OpenRouter) и итог в рублях, баланс OpenRouter, расход токенов
 * по дням, списание аккаунтов и курс доллара. Баланс и токены OpenRouter общие для
 * платформы: при openrouter = null (организация не платформы) блок не показывается.
 */
import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Wallet } from 'lucide-react';
import { PageLayout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { ExpenseItemsCards } from '@/components/expenses/ExpenseItemsCards';
import { OpenRouterBalanceCard } from '@/components/expenses/OpenRouterBalanceCard';
import { TokensByDayChart } from '@/components/expenses/TokensByDayChart';
import { TokensModelsList } from '@/components/expenses/TokensModelsList';
import { UsdRateField } from '@/components/expenses/UsdRateField';
import { WriteOffsBlock } from '@/components/expenses/WriteOffsBlock';
import { useExpenses, useMonthNavigation } from '@/hooks/useExpenses';
import { formatMonthLabel } from '@/lib/expenses';

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-lg bg-white p-4 shadow sm:p-6">
      <h3 className="mb-3 text-base font-semibold text-gray-900">{title}</h3>
      {children}
    </section>
  );
}

export function ExpensesPage(): JSX.Element {
  const { month, prev, next, canGoNext } = useMonthNavigation();
  const { data, isLoading, isError } = useExpenses(month);
  const monthLabel = formatMonthLabel(month);

  useEffect(() => {
    document.title = 'Расходы - SMETA AI';
  }, []);

  return (
    <PageLayout
      data-testid="expenses-page"
      title="Расходы"
      subtitle={<span className="hidden sm:inline">Прокси, аккаунты и токены за месяц</span>}
      titleIcon={
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600">
          <Wallet className="h-5 w-5 text-white" />
        </div>
      }
      breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Расходы' }]}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Предыдущий месяц"
              data-testid="expenses-month-prev"
              onClick={prev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span
              className="min-w-[140px] text-center text-base font-medium text-gray-900"
              data-testid="expenses-month-label"
              aria-live="polite"
            >
              {monthLabel}
            </span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Следующий месяц"
              data-testid="expenses-month-next"
              disabled={!canGoNext}
              onClick={next}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {data && <UsdRateField rate={data.usd_rub_rate} />}
        </div>

        {isLoading && <p className="text-sm text-gray-500">Загрузка...</p>}
        {isError && (
          <p className="text-sm text-red-700" data-testid="expenses-error">
            Не удалось загрузить расходы
          </p>
        )}

        {data && (
          <>
            <ExpenseItemsCards items={data.items} totalRub={data.total_rub} />
            {data.openrouter && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <OpenRouterBalanceCard balance={data.openrouter} />
                <div className="lg:col-span-2">
                  <Section title="Токены OpenRouter по дням">
                    <TokensByDayChart days={data.tokens_by_day} />
                    <TokensModelsList item={data.items.find((i) => i.code === 'tokens')} />
                  </Section>
                </div>
              </div>
            )}
            <Section title="Списание аккаунтов">
              <WriteOffsBlock
                writeOffs={data.write_offs}
                avgLifetimeDays={data.write_off_avg_lifetime_days}
                monthLabel={monthLabel}
              />
            </Section>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default ExpensesPage;
